<template>
  <v-card class="mx-auto" width="95%" style="margin-bottom: 15px;">
    <v-container fluid class="my-5">
      <v-card-title>
        <v-icon left>mdi-bug</v-icon>
        {{ $t('components.ErrorLogViewer.title') }}
        <v-spacer></v-spacer>
        <v-btn color="warning" @click="cleanupOldErrors" :loading="cleaning">
          <v-icon left>mdi-delete-sweep</v-icon>
          {{ $t('components.ErrorLogViewer.cleanupOld') }}
        </v-btn>
        <v-btn color="secondary" @click="refreshLogs" :loading="loading" class="ml-2">
          <v-icon left>mdi-refresh</v-icon>
          {{ $t('components.ErrorLogViewer.refresh') }}
        </v-btn>

        <!-- Bulk Actions -->
        <template v-if="selectedErrors.length > 0">
          <v-divider vertical class="ml-4 mr-2"></v-divider>
          <v-chip color="primary" outlined class="ml-2">
            {{ selectedErrors.length }} {{ $t('components.ErrorLogViewer.selected') }}
          </v-chip>
          <v-btn
            color="success"
            @click="bulkUpdateStatus(true)"
            :loading="bulkUpdating"
            class="ml-2"
            small
          >
            <v-icon left>mdi-check-circle</v-icon>
            {{ $t('components.ErrorLogViewer.markResolved') }}
          </v-btn>
          <v-btn
            color="error"
            @click="bulkUpdateStatus(false)"
            :loading="bulkUpdating"
            class="ml-2"
            small
          >
            <v-icon left>mdi-alert-circle</v-icon>
            {{ $t('components.ErrorLogViewer.markOpen') }}
          </v-btn>
          <v-btn
            text
            @click="clearSelection"
            :disabled="bulkUpdating"
            class="ml-2"
            small
          >
            <v-icon left>mdi-close</v-icon>
            {{ $t('components.ErrorLogViewer.clear') }}
          </v-btn>
        </template>
      </v-card-title>

      <!-- Filters -->
      <v-card-text>
        <v-row>
          <v-col cols="12" md="2">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.startDate"
                  :label="$t('components.ErrorLogViewer.startDate')"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  clearable
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.startDate"
                @input="startDateMenu = false; loadErrorLogsData()"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.endDate"
                  :label="$t('components.ErrorLogViewer.endDate')"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  clearable
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.endDate"
                @input="endDateMenu = false; loadErrorLogsData()"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="filters.errorType"
              :items="errorTypes"
              :label="$t('components.ErrorLogViewer.errorType')"
              prepend-icon="mdi-filter"
              clearable
              @change="loadErrorLogsData"
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="filters.resolved"
              :items="resolvedOptions"
              :label="$t('components.ErrorLogViewer.status')"
              prepend-icon="mdi-check-circle"
              clearable
              @change="loadErrorLogsData"
            ></v-select>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.search"
              :label="$t('components.ErrorLogViewer.searchPlaceholder')"
              prepend-icon="mdi-magnify"
              clearable
              @input="debounceLoadLogs"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Data Table -->
      <v-data-table
        :headers="headers"
        :items="errorLogs"
        :loading="loading"
        :server-items-length="totalCount"
        :page.sync="page"
        :items-per-page.sync="itemsPerPage"
        @update:page="loadErrorLogsData"
        @update:items-per-page="loadErrorLogsData"
        class="elevation-1"
        :expanded.sync="expanded"
        show-expand
        show-select
        v-model="selectedErrors"
        item-key="id"
        :footer-props="{
          itemsPerPageOptions: [10, 25, 50, 100]
        }"
      >
        <!-- Error Type Column -->
        <template v-slot:item.errorType="{ item }">
          <v-chip
            :color="getErrorTypeColor(item.errorType)"
            dark
            small
          >
            {{ item.errorType }}
          </v-chip>
        </template>

        <!-- Message Column -->
        <template v-slot:item.message="{ item }">
          <div class="text-truncate" style="max-width: 300px;">
            {{ item.message }}
          </div>
        </template>

        <!-- User Column -->
        <template v-slot:item.userEmail="{ item }">
          <span v-if="item.userEmail">{{ item.userEmail }}</span>
          <span v-else class="grey--text">{{ $t('components.ErrorLogViewer.anonymous') }}</span>
        </template>

        <!-- Timestamp Column -->
        <template v-slot:item.timestamp="{ item }">
          <span v-if="item.timestamp">
            {{ formatDate(item.timestamp) }}
          </span>
        </template>

        <!-- Resolved Column -->
        <template v-slot:item.resolved="{ item }">
          <v-chip
            :color="item.resolved ? 'success' : 'error'"
            dark
            small
            @click="toggleResolved(item)"
            class="cursor-pointer"
          >
            <v-icon small left>
              {{ item.resolved ? 'mdi-check-circle' : 'mdi-alert-circle' }}
            </v-icon>
            {{ item.resolved ? $t('components.ErrorLogViewer.resolved') : $t('components.ErrorLogViewer.open') }}
          </v-chip>
        </template>

        <!-- Expanded Content -->
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-4">
            <v-card flat>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <h4>{{ $t('components.ErrorLogViewer.errorDetails') }}</h4>
                    <p><strong>{{ $t('components.ErrorLogViewer.message') }}:</strong> {{ item.message }}</p>
                    <p><strong>{{ $t('components.ErrorLogViewer.errorName') }}:</strong> {{ item.name }}</p>
                    <p><strong>{{ $t('components.ErrorLogViewer.url') }}:</strong> {{ item.url }}</p>
                    <p><strong>{{ $t('components.ErrorLogViewer.route') }}:</strong> {{ item.route || $t('components.ErrorLogViewer.unknown') }}</p>
                    <p><strong>{{ $t('components.ErrorLogViewer.userAgent') }}:</strong> {{ item.userAgent }}</p>
                    <p v-if="item.resolvedAt">
                      <strong>{{ $t('components.ErrorLogViewer.resolvedAt') }}:</strong> {{ formatDate(item.resolvedAt) }}
                    </p>
                  </v-col>
                  <v-col cols="12" md="6">
                    <h4>{{ $t('components.ErrorLogViewer.context') }}</h4>
                    <pre v-if="item.context" class="grey lighten-4 pa-2">{{ JSON.stringify(item.context, null, 2) }}</pre>
                  </v-col>
                </v-row>
                <v-row v-if="item.stack">
                  <v-col cols="12">
                    <h4>{{ $t('components.ErrorLogViewer.stackTrace') }}</h4>
                    <pre class="grey lighten-4 pa-2" style="white-space: pre-wrap; word-break: break-word;">{{ item.stack }}</pre>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </td>
        </template>

        <!-- No Data -->
        <template v-slot:no-data>
          <div class="text-center pa-4">
            <v-icon size="64" color="grey">mdi-bug-outline</v-icon>
            <p class="grey--text mt-2">{{ $t('components.ErrorLogViewer.noErrorLogs') }}</p>
          </div>
        </template>
      </v-data-table>
    </v-container>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
    </v-snackbar>

    <!-- Cleanup Confirmation Dialog -->
    <v-dialog v-model="cleanupDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon left color="warning">mdi-delete-sweep</v-icon>
          {{ $t('components.ErrorLogViewer.cleanupOldErrors') }}
        </v-card-title>
        <v-card-text>
          <p>{{ $t('components.ErrorLogViewer.cleanupMessage', { days: cleanupDays }) }}</p>
          <v-text-field
            v-model.number="cleanupDays"
            :label="$t('components.ErrorLogViewer.daysToKeep')"
            type="number"
            min="1"
            max="365"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cleanupDialog = false">{{ $t('components.ErrorLogViewer.cancel') }}</v-btn>
          <v-btn color="warning" @click="confirmCleanup" :loading="cleaning">{{ $t('components.ErrorLogViewer.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import firebase from '@/firebase'

const functions = firebase.functions

export default {
  name: 'ErrorLogViewer',
  data () {
    return {
      loading: false,
      cleaning: false,
      cleanupDialog: false,
      cleanupDays: 30,
      errorLogs: [],
      totalCount: 0,
      page: 1,
      itemsPerPage: 25,
      expanded: [],
      selectedErrors: [],
      bulkUpdating: false,
      startDateMenu: false,
      endDateMenu: false,
      snack: false,
      snackColor: '',
      snackText: '',
      filters: {
        startDate: null,
        endDate: null,
        errorType: null,
        resolved: null,
        search: ''
      },
      searchTimeout: null
    }
  },
  computed: {
    headers () {
      return [
        { text: this.$t('components.ErrorLogViewer.type'), value: 'errorType', sortable: false, width: 100 },
        { text: this.$t('components.ErrorLogViewer.message'), value: 'message', sortable: false },
        { text: this.$t('components.ErrorLogViewer.user'), value: 'userEmail', sortable: false, width: 200 },
        { text: this.$t('components.ErrorLogViewer.timestamp'), value: 'timestamp', sortable: false, width: 180 },
        { text: this.$t('components.ErrorLogViewer.status'), value: 'resolved', sortable: false, width: 120 },
        { text: '', value: 'data-table-expand' }
      ]
    },
    errorTypes () {
      return [
        { text: this.$t('components.ErrorLogViewer.javascript'), value: 'javascript' },
        { text: this.$t('components.ErrorLogViewer.vue'), value: 'vue' },
        { text: this.$t('components.ErrorLogViewer.promise'), value: 'promise' },
        { text: this.$t('components.ErrorLogViewer.manual'), value: 'manual' },
        { text: this.$t('components.ErrorLogViewer.warning'), value: 'warning' },
        { text: this.$t('components.ErrorLogViewer.info'), value: 'info' }
      ]
    },
    resolvedOptions () {
      return [
        { text: this.$t('components.ErrorLogViewer.open'), value: false },
        { text: this.$t('components.ErrorLogViewer.resolved'), value: true }
      ]
    }
  },
  mounted () {
    this.loadErrorLogsData()
  },
  methods: {
    async loadErrorLogsData () {
      this.loading = true
      try {
        const getErrorLogsFunction = functions.httpsCallable('errorLogs-getErrorLogs')

        const params = {
          limit: this.itemsPerPage,
          offset: (this.page - 1) * this.itemsPerPage,
          ...this.filters
        }

        if (this.filters.search) {
          params.search = this.filters.search
        }

        const result = await getErrorLogsFunction(params)

        this.errorLogs = result.data.errorLogs
        this.totalCount = result.data.totalCount

        // Clear selection when data is reloaded
        this.selectedErrors = []
      } catch (error) {
        console.error('Error loading error logs:', error)
        this.showSnackbar(this.$t('components.ErrorLogViewer.failedToLoadLogs'), 'error')
      } finally {
        this.loading = false
      }
    },

    async refreshLogs () {
      this.page = 1
      await this.loadErrorLogsData()
    },

    async toggleResolved (item) {
      try {
        const resolveErrorFunction = functions.httpsCallable('errorLogs-resolveError')
        await resolveErrorFunction({
          errorId: item.id,
          resolved: !item.resolved
        })

        // Update local item
        item.resolved = !item.resolved

        this.showSnackbar(this.$t('components.ErrorLogViewer.errorMarkedAs', { status: item.resolved ? this.$t('components.ErrorLogViewer.resolved') : this.$t('components.ErrorLogViewer.open') }), 'success')
      } catch (error) {
        console.error('Error updating error status:', error)
        this.showSnackbar(this.$t('components.ErrorLogViewer.failedToUpdateStatus'), 'error')
      }
    },

    async bulkUpdateStatus (resolved) {
      if (this.selectedErrors.length === 0) {
        this.showSnackbar(this.$t('components.ErrorLogViewer.noErrorsSelected'), 'warning')
        return
      }

      this.bulkUpdating = true
      try {
        const bulkResolveFunction = functions.httpsCallable('errorLogs-bulkResolveErrors')
        const errorIds = this.selectedErrors.map(error => error.id)

        const result = await bulkResolveFunction({
          errorIds: errorIds,
          resolved: resolved
        })

        // Update local items
        this.selectedErrors.forEach(selectedError => {
          const errorIndex = this.errorLogs.findIndex(error => error.id === selectedError.id)
          if (errorIndex !== -1) {
            this.errorLogs[errorIndex].resolved = resolved
          }
        })

        // Clear selection
        this.selectedErrors = []

        var statusText = resolved ? this.$t('components.ErrorLogViewer.resolved') : this.$t('components.ErrorLogViewer.open')
        this.showSnackbar(this.$t('components.ErrorLogViewer.errorsMarkedAs', { count: result.data.updated, status: statusText }), 'success')
      } catch (error) {
        console.error('Error bulk updating errors:', error)
        this.showSnackbar(this.$t('components.ErrorLogViewer.failedBulkUpdate'), 'error')
      } finally {
        this.bulkUpdating = false
      }
    },

    clearSelection () {
      this.selectedErrors = []
    },

    cleanupOldErrors () {
      this.cleanupDialog = true
    },

    async confirmCleanup () {
      this.cleaning = true
      try {
        const cleanupFunction = functions.httpsCallable('errorLogs-cleanupErrorLogs')
        const result = await cleanupFunction({
          daysOld: this.cleanupDays
        })

        this.showSnackbar(this.$t('components.ErrorLogViewer.deletedOldLogs', { count: result.data.deleted }), 'success')

        this.cleanupDialog = false
        await this.loadErrorLogsData()
      } catch (error) {
        console.error('Error cleaning up logs:', error)
        this.showSnackbar(this.$t('components.ErrorLogViewer.failedCleanup'), 'error')
      } finally {
        this.cleaning = false
      }
    },

    debounceLoadLogs () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.page = 1
        this.loadErrorLogsData()
      }, 500)
    },

    formatDate (date) {
      if (!date) return 'N/A'

      // Handle different date formats
      let dateObj
      if (date.toDate) {
        // Firestore Timestamp object
        dateObj = date.toDate()
      } else if (typeof date === 'string') {
        // ISO string from backend
        dateObj = new Date(date)
      } else {
        // Already a Date object
        dateObj = new Date(date)
      }

      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        return 'N/A'
      }

      return dateObj.toLocaleString()
    },

    showSnackbar (text, color = 'info') {
      this.snackText = text
      this.snackColor = color
      this.snack = true
    },

    getErrorTypeColor (errorType) {
      switch (errorType) {
        case 'javascript': return 'red'
        case 'vue': return 'green'
        case 'promise': return 'orange'
        case 'manual': return 'blue'
        case 'warning': return 'amber'
        case 'info': return 'cyan'
        default: return 'grey'
      }
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
